import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { animate, motion, useMotionValue } from "motion/react";
import { readFragment, ResultOf } from "gql.tada";
import { SlideCarouselSectionById } from "@queries/entries/SlideCarouselSectionById";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Link } from "@components/shared/core/Link";
import { Button } from "@components/shared/core/Button";
import { animationCurves } from "@utils/animationCurves";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { MarkdownLite } from "@components/shared/MarkdownLite";

type SlideCarouselData = NonNullable<
  ResultOf<typeof SlideCarouselSectionById>["slideCarouselSection"]
>;

type SlideItem = NonNullable<
  NonNullable<SlideCarouselData["itemsCollection"]>["items"][number]
>;

// ─── Card ────────────────────────────────────────────────────────────────────

const SlideCard = ({
  item,
  index,
  totalItems,
  isActive,
  direction,
  onMouseEnter,
  onMouseLeave,
  slideRef,
}: {
  item: SlideItem;
  index: number;
  totalItems: number;
  isActive: boolean;
  direction: "left" | "right";
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  slideRef: (node: HTMLDivElement | null) => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Horizontal fill bar: scaleX 0→1, origin switches based on travel direction
  const barScaleX = useMotionValue(0);
  const barOrigin = useMotionValue("left");

  useEffect(() => {
    // Entry: fill enters from the side the user came from (opposite of travel direction)
    //   going right → new card fill enters from the left
    //   going left  → new card fill enters from the right
    // Exit: fill chases toward the travel direction
    //   going right → old card fill exits to the right
    //   going left  → old card fill exits to the left
    const origin = isActive
      ? (direction === "right" ? "left" : "right")
      : (direction === "right" ? "right" : "left");
    barOrigin.set(origin);
    animate(barScaleX, isActive ? 1 : 0, animationCurves.macro);
  }, [isActive, direction, barScaleX, barOrigin]);

  // Category text reveal: clip-path synced with fill direction
  const clipPath = useMotionValue("polygon(0 0, 0% 0, 0% 100%, 0 100%)");
  useEffect(() => {
    return barScaleX.on("change", (s) => {
      const origin = barOrigin.get();
      clipPath.set(
        origin === "left"
          ? `polygon(0 0, ${s * 100}% 0, ${s * 100}% 100%, 0 100%)`
          : `polygon(${(1 - s) * 100}% 0, 100% 0, 100% 100%, ${(1 - s) * 100}% 100%)`
      );
    });
  }, [barScaleX, barOrigin, clipPath]);

  // Video play/pause driven by active state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [isActive]);

  const ctaLink = readFragment(LinkFragment, item.ctaLink);
  const videoLink = item.videoLink ? readFragment(LinkFragment, item.videoLink) : null;
  const imageIsVideo = item.image?.contentType?.startsWith("video/");
  const imageUrl = imageIsVideo ? undefined : item.image?.url;
  const mediaVideoUrl = imageIsVideo ? item.image?.url : videoLink?.url;
  const hasVideo = Boolean(mediaVideoUrl);

  return (
    <div
      ref={slideRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="snap-start shrink-0 w-[72vw] sm:w-[27rem] lg:w-[29rem] xl:w-[31rem] rounded-[0.75rem] bg-neutral-light/20 overflow-hidden select-none"
    >
      <article
        role="group"
        aria-roledescription="slide"
        aria-label={`Slide ${index + 1} of ${totalItems}`}
        className="h-full flex flex-col"
      >
        {/* Image / video — no fill overlay here */}
        <div className="relative aspect-[16/10] overflow-hidden bg-brand-black">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={item.image?.title ?? item.title ?? "News visual"}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {hasVideo && mediaVideoUrl && (
            <video
              ref={videoRef}
              src={mediaVideoUrl}
              className="absolute inset-0 h-full w-full object-cover"
              muted
              loop
              playsInline
              preload="auto"
              poster={imageUrl ?? undefined}
            />
          )}
        </div>

        {/* Content area — red fill sweeps directionally on hover/active */}
        <div className="flex flex-col flex-1 p-5 dsk:p-6 relative overflow-hidden">
          {/* Fill bar */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{ scaleX: barScaleX, transformOrigin: barOrigin, backgroundColor: "rgb(var(--palette-current-deep) / var(--tw-bg-opacity, 1))" }}
          />

          {/* Content above the fill */}
          <div className="relative z-10 flex flex-col flex-1">
            {/* Category: red by default, white revealed by fill */}
            <div className="relative">
              <p className="text-label uppercase tracking-[0.12em] text-brand-red">
                {item.category}
              </p>
              <motion.p
                className="text-label uppercase tracking-[0.12em] text-brand-cream absolute inset-0 select-none"
                style={{ clipPath }}
                aria-hidden
              >
                {item.category}
              </motion.p>
            </div>

            <h3 className="text-heading-4 mt-3 text-balance text-brand-cream">
              {item.title}
            </h3>
            <p className="text-body-small text-brand-cream/75 mt-4 max-w-[52ch]">
              <MarkdownLite>{item.description}</MarkdownLite>
            </p>

            <div className="mt-auto pt-6 flex items-center justify-between gap-4">
              {item.advertisingTopic ? (
                <span className="text-label text-brand-cream/50">
                  {item.advertisingTopic}
                </span>
              ) : (
                <span />
              )}
              {ctaLink && (
                <Button asChild variant="light-on-blur" size="small" className="shrink-0">
                  <Link link={ctaLink}>{ctaLink.text ?? "Learn more"}</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

// ─── Section ─────────────────────────────────────────────────────────────────

export const NewsReelSection = ({ data }: { data: SlideCarouselData }) => {
  const items = useMemo(
    () => data.itemsCollection?.items.filter(Boolean) ?? [],
    [data],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  // visualIndex: steps sequentially through intermediate cards when skipping
  const [visualIndex, setVisualIndex] = useState<number | null>(null);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isDesktop = useIsDesktop();

  const visualIndexRef = useRef<number | null>(null);
  // Persists through mouse leave so direction is correct on next card entry
  const lastHoveredIndexRef = useRef<number | null>(null);
  const stepTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const prevActiveIndex = useRef(0);

  const clearStepTimers = useCallback(() => {
    stepTimersRef.current.forEach(clearTimeout);
    stepTimersRef.current = [];
  }, []);

  const handleHoverEnter = useCallback((targetIndex: number) => {
    clearStepTimers();

    // Direction: always computed from the last card touched (survives mouse leave)
    const last = lastHoveredIndexRef.current;
    if (last !== null && last !== targetIndex) {
      setDirection(targetIndex > last ? "right" : "left");
    }

    // Sequential stepping source: active visual position, or last known card if re-entering
    const stepFrom = visualIndexRef.current ?? lastHoveredIndexRef.current;
    lastHoveredIndexRef.current = targetIndex;

    if (stepFrom !== null && Math.abs(targetIndex - stepFrom) > 1) {
      // Step through intermediate cards (300 ms each)
      const step = targetIndex > stepFrom ? 1 : -1;
      const STEP_MS = 300;
      let delay = 0;
      for (let i = stepFrom + step; i !== targetIndex + step; i += step) {
        const idx = i;
        stepTimersRef.current.push(
          setTimeout(() => {
            visualIndexRef.current = idx;
            setVisualIndex(idx);
          }, delay)
        );
        delay += STEP_MS;
      }
    } else {
      visualIndexRef.current = targetIndex;
      setVisualIndex(targetIndex);
    }
  }, [clearStepTimers]);

  const handleHoverLeave = useCallback(() => {
    clearStepTimers();
    visualIndexRef.current = null;
    setVisualIndex(null);
    // lastHoveredIndexRef intentionally kept — needed for correct direction on next entry
  }, [clearStepTimers]);

  // Track active slide from scroll position, updating direction on change
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const updateActiveSlide = () => {
      const scrollLeft = track.scrollLeft;
      const styles = window.getComputedStyle(track);
      const paddingLeft = Number.parseFloat(styles.paddingLeft || "0");
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      slideRefs.current.forEach((slide, index) => {
        if (!slide) return;
        const slideStart = slide.offsetLeft - paddingLeft;
        const distance = Math.abs(slideStart - scrollLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex !== prevActiveIndex.current) {
        setDirection(nearestIndex > prevActiveIndex.current ? "right" : "left");
        prevActiveIndex.current = nearestIndex;
      }
      setActiveIndex(nearestIndex);
    };

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateActiveSlide);
    };

    updateActiveSlide();
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateActiveSlide);

    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateActiveSlide);
    };
  }, [items.length]);

  // Mouse drag-to-scroll
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onMouseDown = (e: MouseEvent) => {
      isDragging.current = true;
      dragStartX.current = e.pageX - track.offsetLeft;
      dragScrollLeft.current = track.scrollLeft;
      track.style.cursor = "grabbing";
      track.style.scrollSnapType = "none";
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = x - dragStartX.current;
      track.scrollLeft = dragScrollLeft.current - walk;
    };

    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      track.style.cursor = "";
      track.style.scrollSnapType = "";
    };

    track.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      track.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    const target = slideRefs.current[index];
    if (!track || !target) return;

    const styles = window.getComputedStyle(track);
    const paddingLeft = Number.parseFloat(styles.paddingLeft || "0");

    track.scrollTo({
      left: target.offsetLeft - paddingLeft,
      behavior: "smooth",
    });
  };

  // Counter shows highlighted card on desktop hover, scroll position otherwise
  const displayIndex = isDesktop && visualIndex !== null ? visualIndex : activeIndex;

  const canGoBack = activeIndex > 0;
  const canGoForward = activeIndex < items.length - 1;
  const colOffset = "max(18px, calc(48px + (100vw - 432px) / 24))";

  return (
    <section
      aria-labelledby="newsreel-title"
      className="bg-brand-black text-brand-cream pt-32 dsk:pt-48 pb-10 dsk:pb-20 overflow-hidden"
      style={{ "--carousel-offset": colOffset } as React.CSSProperties}
    >
      <div
        role="region"
        aria-roledescription="carousel"
        aria-label={data.title ?? "News carousel"}
        aria-describedby="newsreel-status"
      >
        <div className="grid-container mb-5 dsk:mb-6" style={{ paddingLeft: "var(--carousel-offset)", paddingRight: "var(--carousel-offset)" }}>
          <div className="col-span-full flex flex-col min-[481px]:flex-row min-[481px]:items-end min-[481px]:justify-between gap-4">
            <div className="max-w-3xl">
              <h2 id="newsreel-title" className="text-heading-3 min-[768px]:text-heading-2 text-balance">
                {data.title}
              </h2>
            </div>
            <div role="group" aria-label="Slide controls" className="flex items-center justify-end max-[480px]:justify-between gap-2 w-full min-[481px]:w-auto">
              <div className="text-body-small text-brand-cream/70 mr-1 whitespace-nowrap self-center">
                <span id="newsreel-status" className="sr-only" aria-live="polite">
                  Slide {displayIndex + 1} of {items.length}
                </span>
                <span aria-hidden="true">{displayIndex + 1} of {items.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="light-on-blur"
                  size="inherit"
                  disabled={!canGoBack}
                  aria-label="Previous slide"
                  aria-controls="newsreel-track"
                  onClick={() => { if (canGoBack) { setDirection("left"); scrollToIndex(activeIndex - 1); } }}
                  className="size-12 rotate-180 disabled:opacity-40"
                >
                  <span className="flex justify-center items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" fill="none">
                      <path d="m8.5 1 7 7-7 7M14.833 8H1.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </Button>
                <Button
                  variant="light-on-blur"
                  size="inherit"
                  disabled={!canGoForward}
                  aria-label="Next slide"
                  aria-controls="newsreel-track"
                  onClick={() => { if (canGoForward) { setDirection("right"); scrollToIndex(activeIndex + 1); } }}
                  className="size-12 disabled:opacity-40"
                >
                  <span className="flex justify-center items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" fill="none">
                      <path d="m8.5 1 7 7-7 7M14.833 8H1.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="relative left-1/2 w-screen -translate-x-1/2 overflow-hidden">
          <div
            id="newsreel-track"
            ref={trackRef}
            className="flex gap-4 dsk:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 cursor-grab [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{
              paddingLeft: "var(--carousel-offset)",
              paddingRight: "var(--carousel-offset)",
              scrollPaddingLeft: "var(--carousel-offset)",
              scrollPaddingRight: "var(--carousel-offset)",
            }}
          >
            {items.map((item, index) => {
              if (!item) return null;
              const isActive = isDesktop ? visualIndex === index : activeIndex === index;
              return (
                <SlideCard
                  key={item.sys.id}
                  item={item}
                  index={index}
                  totalItems={items.length}
                  isActive={isActive}
                  direction={direction}
                  onMouseEnter={() => isDesktop && handleHoverEnter(index)}
                  onMouseLeave={() => isDesktop && handleHoverLeave()}
                  slideRef={(node) => { slideRefs.current[index] = node; }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
